import React, { useCallback, useMemo } from "react";
import { RouteComponentProps, useHistory } from "react-router";

import ContractEditor, {
  ContractEditorState,
  SigneeType,
  defaultSigneesAvailable,
} from "../components/contracts/ContractEditor";
import { ContractEditorProvider } from "../components/contracts/ContractEditorContext";
import ContractEditorVariablesFormContextBar from "../components/contracts/ContractEditorVariablesFormContextBar";
import { ScreenContainer } from "../components/wombat-ui";
import useAddNotification from "../hooks/useAddNotification";

import useInsertContract from "../hooks/useInsertContract";
import useTranslations from "../hooks/useTranslations";
import useViewer from "../hooks/useViewer";

type EditContractScreenProps = RouteComponentProps<{
  contractTemplateId: string;
  madId: string;
}>;

const EditContractScreen: React.FC<EditContractScreenProps> = ({ match }) => {
const {
    id,
    company: { contractTemplates },
  } = useViewer();

  const contract = useMemo(() => {
    return contractTemplates.find(
      ({ id }) => id === match.params.contractTemplateId
    );
  }, [contractTemplates]);

  const t = useTranslations();
  const history = useHistory();
  const [insertContract] = useInsertContract();
  const [addNotification] = useAddNotification();
  
  const handleContractGenerated = useCallback(
    (b64PDF: string, filename: string, withDocusign: boolean) => {
      insertContract({
        variables: {
          input: {
            madId: match.params.madId,
            contractTemplateId: match.params.contractTemplateId,
            document: b64PDF,
            signingProcess: withDocusign ? "docusign" : "manual",
            filename,
          },
        },
      })
        .then(() => {
          addNotification({
            variables: {
              title: t("contracts.notifications.edit.success.title") as string,
              body: t("contracts.notifications.edit.success.body") as string,
              status: "success",
            },
          });
          history.push(`/mads/${match.params.madId}`);
        })
        .catch(() => {
          addNotification({
            variables: {
              title: t("contracts.notifications.edit.error.title") as string,
              body: t("contracts.notifications.edit.error.body") as string,
              status: "error",
            },
          });
        });
    },
    [insertContract, match.params, history, id, contract]
  );

  const deserializedState = useMemo((): ContractEditorState | undefined => {
    if (contract)
      return {
        ...JSON.parse(contract.body),
        title: contract.title,
        signatories: defaultSigneesAvailable.filter(
          (s) => contract.signatories.indexOf(s) === -1
        ),
      };
    return undefined;
  }, [contract]);

  return (
    <ContractEditorProvider defaultState={deserializedState?.variables}>
      <ScreenContainer loading={!contract}>
        <div className="flex">
          <div className="flex-grow">
            <ContractEditor
              state={deserializedState}
              onContractGeneration={handleContractGenerated}
            />
          </div>
          <div className="flex-shrink">
            <ContractEditorVariablesFormContextBar
              contract={deserializedState}
            />
          </div>
        </div>
      </ScreenContainer>
    </ContractEditorProvider>
  );
};

export default React.memo(EditContractScreen);
